import { useEffect, useState, useCallback, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Wallet, Users, ArrowLeftRight, Check, AlertCircle, Calendar, Banknote, Plus, Trash2, Star, AlertTriangle, Home, Heart, Tag, Pencil, ChevronRight, TrendingUp, TrendingDown, List, FolderInput, Loader2, Palette } from 'lucide-react';
import DisplayModeToggle from '../components/common/DisplayModeToggle';
import OptionToggle from '../components/common/OptionToggle';
import SegmentedControl from '../components/common/SegmentedControl';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { useCategoryTranslation } from '../hooks/useCategoryTranslation';
import { useAuthStore } from '../store/authStore';
import { useSettingsStore } from '../store/settingsStore';
import { useBankAccountStore } from '../store/bankAccountStore';
import { useSubscriptionStore } from '../store/subscriptionStore';
import { useAutoSave } from '../hooks/useAutoSave';
import { useNavigationGuard } from '../hooks/useNavigationGuard';
import { getPartnerName, useLivingTerms } from '../hooks/useLivingTerms';
import { useNotificationStore } from '../store/notificationStore';
import AmountInput from '../components/common/AmountInput';
import SaveToast from '../components/common/SaveToast';
import CategoryOperationsModal from '../components/common/CategoryOperationsModal';
import PartnerLinkCard from '../components/common/PartnerLinkCard';
import HouseholdMembersCard from '../components/common/HouseholdMembersCard';
import PartnerIncomeCard from '../components/common/PartnerIncomeCard';
import type { Settings as SettingsType, AccountType, BankAccount, SharedBankAccount, LivingType } from '../types';

// Types pour les catégories personnalisées
interface UserSubcategory {
  id: string;
  name: string;
  icon: string | null;
  isDefault: boolean;
  isPersonal: boolean;
  isCouple: boolean;
  usageCount: number;
}

interface UserCategory {
  id: string;
  name: string;
  icon: string | null;
  isDefault?: boolean;
  isPersonal: boolean;
  isCouple: boolean;
  usageCount: number;
  subcategories: UserSubcategory[];
}

interface UserCategories {
  income: UserCategory[];
  expense: UserCategory[];
}

// Dialog de confirmation de suppression de compte
interface DeleteDialogState {
  isOpen: boolean;
  accountId: string | null;
  accountName: string;
  operationsCount: number;
}

function DeleteAccountDialog({
  isOpen,
  accountName,
  operationsCount,
  onConfirm,
  onCancel,
}: {
  isOpen: boolean;
  accountName: string;
  operationsCount: number;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} />
      <div className="relative bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-red-100 rounded-full">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">{t('bankAccounts.deleteTitle')}</h3>
        </div>
        <p className="text-gray-600 mb-4">
          <Trans t={t} i18nKey="bankAccounts.deleteMessage" values={{ name: accountName }} components={{ strong: <strong /> }} />
        </p>
        {operationsCount > 0 && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg mb-4">
            <p className="text-red-700 text-sm font-medium">
              {t('bankAccounts.deleteOperationsWarning', { count: operationsCount })}
            </p>
          </div>
        )}
        <p className="text-sm text-gray-500 mb-6">
          {t('bankAccounts.irreversible')}
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium"
          >
            {tc('cancel')}
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
          >
            {tc('delete')}
          </button>
        </div>
      </div>
    </div>
  );
}

// Dialog de confirmation de suppression de catégorie
interface DeleteCategoryDialogState {
  isOpen: boolean;
  categoryId: string | null;
  categoryName: string;
  usageCount: number;
  type: 'income' | 'expense';
  isSubcategory: boolean;
  parentCategoryId?: string;
}

function DeleteCategoryDialog({
  isOpen,
  categoryName,
  usageCount,
  isSubcategory,
  onConfirm,
  onCancel,
  onReassign,
}: {
  isOpen: boolean;
  categoryName: string;
  usageCount: number;
  isSubcategory: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  onReassign?: () => void;
}) {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  if (!isOpen) return null;

  const label = isSubcategory ? t('categories.subcategory') : t('categories.category');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} />
      <div className="relative bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-red-100 rounded-full">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">{t('categories.deleteCategoryTitle', { type: label })}</h3>
        </div>
        <p className="text-gray-600 mb-4">
          {t('categories.deleteConfirmMessage', { type: label, name: categoryName })}
        </p>
        {usageCount > 0 && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
            <p className="text-amber-700 text-sm font-medium">
              {t('categories.deleteWarning', { type: label, count: usageCount })}
              {!isSubcategory && ` ${t('categories.deleteSubcategoriesWarning')}`}
            </p>
          </div>
        )}
        <p className="text-sm text-gray-500 mb-6">
          {t('categories.operationsLoseCategory', { type: label })}
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium"
          >
            {tc('cancel')}
          </button>
          {usageCount > 0 && onReassign && (
            <button
              onClick={onReassign}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium"
            >
              {t('categories.reassignBeforeDelete')}
            </button>
          )}
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
          >
            {tc('delete')}
          </button>
        </div>
      </div>
    </div>
  );
}

// Dialog de déplacement de sous-catégorie
function MoveSubcategoryDialog({
  isOpen,
  subcategoryName,
  currentParentId,
  type,
  targetCategoryId,
  onTargetChange,
  onConfirm,
  onCancel,
  isLoading,
}: {
  isOpen: boolean;
  subcategoryName: string;
  currentParentId: string;
  type: 'income' | 'expense';
  targetCategoryId: string;
  onTargetChange: (id: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading: boolean;
}) {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const { translateCategoryName } = useCategoryTranslation();
  const [availableCategories, setAvailableCategories] = useState<Array<{ id: string; name: string; isDefault: boolean }>>([]);

  useEffect(() => {
    if (!isOpen) return;
    const fetchAll = async () => {
      try {
        const res = await api.get(`/categories/${type}`);
        if (res.data.success) {
          setAvailableCategories(res.data.data);
        }
      } catch {
        // Handle error
      }
    };
    fetchAll();
  }, [isOpen, type]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} />
      <div className="relative bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {t('categories.moveSubcategory')}
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          {subcategoryName}
        </p>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('categories.moveTarget')}</label>
          <select
            value={targetCategoryId}
            onChange={(e) => onTargetChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">{t('categories.moveTarget')}</option>
            {availableCategories
              .filter((cat) => cat.id !== currentParentId)
              .map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {translateCategoryName(cat.id, cat.name, cat.isDefault)}
                </option>
              ))}
          </select>
        </div>
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium"
          >
            {tc('cancel')}
          </button>
          <button
            onClick={onConfirm}
            disabled={!targetCategoryId || isLoading}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium disabled:opacity-50 flex items-center gap-1.5"
          >
            {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
            {t('categories.moveSubcategory')}
          </button>
        </div>
      </div>
    </div>
  );
}

// Composant pour afficher une catégorie ou sous-catégorie éditable
function CategoryItem({
  name,
  categoryId,
  isDefaultCategory,
  usageCount,
  isEditing,
  editValue,
  onEditChange,
  onEditStart,
  onEditSave,
  onEditCancel,
  onDelete,
  onViewOperations,
  onMoveSubcategory,
  isSubcategory = false,
  readOnly = false,
}: {
  name: string;
  categoryId?: string;
  isDefaultCategory?: boolean;
  usageCount: number;
  isEditing: boolean;
  editValue: string;
  onEditChange: (value: string) => void;
  onEditStart: () => void;
  onEditSave: () => void;
  onEditCancel: () => void;
  onDelete: () => void;
  onViewOperations?: () => void;
  onMoveSubcategory?: () => void;
  isSubcategory?: boolean;
  readOnly?: boolean;
}) {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const { translateCategoryName } = useCategoryTranslation();
  const displayName = categoryId ? translateCategoryName(categoryId, name, isDefaultCategory) : name;
  return (
    <div className={`flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 group ${isSubcategory ? 'ml-4 border-l-2 border-gray-200 pl-3' : ''}`}>
      <div className="flex items-center gap-2 flex-1 min-w-0">
        {isSubcategory && <ChevronRight className="w-3 h-3 text-gray-400 flex-shrink-0" />}
        {isEditing ? (
          <input
            type="text"
            value={editValue}
            onChange={(e) => onEditChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onEditSave();
              if (e.key === 'Escape') onEditCancel();
            }}
            className="flex-1 px-2 py-1 text-sm border border-primary-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
            autoFocus
          />
        ) : (
          <span className={`text-sm truncate ${readOnly ? 'text-gray-500 italic' : 'text-gray-700'}`}>{displayName}</span>
        )}
        {!readOnly && (
          <button
            onClick={onViewOperations}
            className="text-xs text-gray-400 flex-shrink-0 bg-gray-100 px-1.5 py-0.5 rounded hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer"
            title={t('categories.viewOperations')}
          >
            {usageCount}
          </button>
        )}
      </div>
      {!readOnly && (
        <div className="flex items-center gap-1 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
          {isEditing ? (
            <>
              <button
                onClick={onEditSave}
                className="p-1 text-green-600 hover:text-green-700 transition-colors"
                title={t('categories.save')}
              >
                <Check className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={onEditCancel}
                className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                title={tc('cancel')}
              >
                <AlertCircle className="w-3.5 h-3.5" />
              </button>
            </>
          ) : (
            <>
              {onViewOperations && (
                <button
                  onClick={onViewOperations}
                  className="p-1 text-gray-400 hover:text-primary-600 transition-colors"
                  title={t('categories.viewOperations')}
                >
                  <List className="w-3.5 h-3.5" />
                </button>
              )}
              {isSubcategory && onMoveSubcategory && (
                <button
                  onClick={onMoveSubcategory}
                  className="p-1 text-gray-400 hover:text-primary-600 transition-colors"
                  title={t('categories.moveSubcategory')}
                >
                  <FolderInput className="w-3.5 h-3.5" />
                </button>
              )}
              <button
                onClick={onEditStart}
                className="p-1 text-gray-400 hover:text-primary-600 transition-colors"
                title={t('categories.rename')}
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={onDelete}
                className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                title={tc('delete')}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Couleurs prédéfinies pour les comptes bancaires
const BANK_COLORS = [
  '#6366f1', // Indigo
  '#8b5cf6', // Violet
  '#ec4899', // Rose
  '#ef4444', // Rouge
  '#f97316', // Orange
  '#eab308', // Jaune
  '#22c55e', // Vert
  '#14b8a6', // Teal
  '#06b6d4', // Cyan
  '#3b82f6', // Bleu
];

// Helper pour formater la date ISO vers format input date (YYYY-MM-DD)
function formatDateForInput(isoDate: string | null): string {
  if (!isoDate) return '';
  return isoDate.split('T')[0];
}

// Types de vie disponibles - translated via t() in component
const getLivingTypes = (t: (key: string) => string): { value: LivingType; label: string; icon: typeof Heart }[] => [
  { value: 'couple', label: t('livingType.couple'), icon: Heart },
  { value: 'colocation', label: t('livingType.colocation'), icon: Home },
];

const getAccountTypes = (livingType: LivingType, t: (key: string) => string): { value: AccountType; label: string; description: string; icon: typeof Wallet }[] => [
  {
    value: 'joint_with_personal',
    label: t('accountType.jointWithPersonal'),
    description: t('accountType.jointWithPersonalDesc'),
    icon: Users,
  },
  {
    value: 'joint_only',
    label: t('accountType.jointOnly'),
    description: t('accountType.jointOnlyDesc'),
    icon: Wallet,
  },
  {
    value: 'personal_with_transfers',
    label: t('accountType.personalWithTransfers'),
    description: livingType === 'couple'
      ? t('accountType.personalWithTransfersDescCouple')
      : t('accountType.personalWithTransfersDescColocation'),
    icon: ArrowLeftRight,
  },
];


type SettingsTabType = 'shared' | 'personal' | 'categories';

export default function Settings() {
  const { t, i18n } = useTranslation('settings');
  const dateLocale = i18n.language === 'fr' ? 'fr-FR' : 'en-US';
  const canCreate = useSubscriptionStore((s) => s.canCreate);
  const effectivePlan = useSubscriptionStore((s) => s.planInfo?.effectivePlan || 'free');
  const [settings, setSettings] = useState<SettingsType | null>(null);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [dirtyAccountIds, setDirtyAccountIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [editingAccount, setEditingAccount] = useState<string | null>(null);
  const [newAccountName, setNewAccountName] = useState('');
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState>({
    isOpen: false,
    accountId: null,
    accountName: '',
    operationsCount: 0,
  });
  const [bankAccountSaveStatus, setBankAccountSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  // État pour les comptes partagés
  const [sharedAccounts, setSharedAccounts] = useState<SharedBankAccount[]>([]);
  const [dirtySharedAccountIds, setDirtySharedAccountIds] = useState<Set<string>>(new Set());
  const [editingSharedAccount, setEditingSharedAccount] = useState<string | null>(null);
  const [newSharedAccountName, setNewSharedAccountName] = useState('');
  const [showAddSharedAccount, setShowAddSharedAccount] = useState(false);
  const [deleteSharedDialog, setDeleteSharedDialog] = useState<DeleteDialogState>({
    isOpen: false,
    accountId: null,
    accountName: '',
    operationsCount: 0,
  });
  const sharedAccountsRef = useRef<SharedBankAccount[]>([]);

  // Ref pour accéder aux comptes bancaires actuels lors de la sauvegarde
  const bankAccountsRef = useRef<BankAccount[]>([]);

  // État pour les catégories personnalisées
  const [userCategories, setUserCategories] = useState<UserCategories>({ income: [], expense: [] });
  const [editingCategory, setEditingCategory] = useState<string | null>(null);
  const [editCategoryValue, setEditCategoryValue] = useState('');
  const [deleteCategoryDialog, setDeleteCategoryDialog] = useState<DeleteCategoryDialogState>({
    isOpen: false,
    categoryId: null,
    categoryName: '',
    usageCount: 0,
    type: 'expense',
    isSubcategory: false,
  });

  // État pour le modal de visualisation des opérations d'une catégorie
  const [operationsModal, setOperationsModal] = useState<{
    isOpen: boolean;
    categoryId: string;
    categoryName: string;
    categoryType: 'income' | 'expense';
    isSubcategory: boolean;
  }>({
    isOpen: false,
    categoryId: '',
    categoryName: '',
    categoryType: 'expense',
    isSubcategory: false,
  });

  // État pour le déplacement de sous-catégorie
  const [moveSubcategoryState, setMoveSubcategoryState] = useState<{
    isOpen: boolean;
    subcategoryId: string;
    subcategoryName: string;
    currentParentId: string;
    type: 'income' | 'expense';
  }>({
    isOpen: false,
    subcategoryId: '',
    subcategoryName: '',
    currentParentId: '',
    type: 'expense',
  });
  const [moveTargetCategoryId, setMoveTargetCategoryId] = useState('');
  const [movingSubcategory, setMovingSubcategory] = useState(false);

  const user = useAuthStore((state) => state.user);
  const hasPartner = user?.hasPartner || false;
  const setGlobalSettings = useSettingsStore((state) => state.setSettings);
  const { setBankAccounts: setGlobalBankAccounts, setSharedBankAccounts: setGlobalSharedBankAccounts } = useBankAccountStore();

  // La section affichée est pilotée par l'URL (?tab=), depuis le sous-menu « Profil ».
  // Le composant reste monté d'un onglet à l'autre : aucune donnée n'est rechargée
  // ni perdue en changeant de section.
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: SettingsTabType =
    tabParam === 'categories' || (tabParam === 'personal' && settings?.accountType !== 'joint_only')
      ? (tabParam as SettingsTabType)
      : 'shared';

  // Le tutoriel amène lui-même sur la bonne section : ses étapes portent l'URL
  // complète (`/dashboard/reglages?tab=…`), la page n'a rien à synchroniser.

  // Utiliser les termes adaptés au type de vie
  const livingType = settings?.livingType ?? 'couple';
  const terms = useLivingTerms(livingType);
  const partner1Name = getPartnerName(1, user?.partner1FirstName, livingType);
  const partner2Name = getPartnerName(2, user?.partner2FirstName, livingType);
  const accountTypes = getAccountTypes(livingType, t);

  // Retours des actions sur le foyer (invitation, noms, revenu…) : en toast,
  // la page est longue et un bandeau en haut passerait inaperçu.
  const addNotification = useNotificationStore((state) => state.addNotification);
  const notify = useCallback((msg: { type: 'success' | 'error'; text: string }) => {
    addNotification({ type: msg.type, title: t(msg.type, { ns: 'common' }), message: msg.text });
  }, [addNotification, t]);

  // Ref pour accéder aux settings actuels dans le callback de sauvegarde
  const settingsRef = useRef<SettingsType | null>(null);
  settingsRef.current = settings;

  // Synchroniser la ref des comptes bancaires
  bankAccountsRef.current = bankAccounts;
  sharedAccountsRef.current = sharedAccounts;

  // Fonction de sauvegarde
  const performSave = useCallback(async () => {
    if (!settingsRef.current) return;
    await api.put('/settings', settingsRef.current);
    // Mettre à jour le store global
    setGlobalSettings(settingsRef.current);
  }, [setGlobalSettings]);

  // Auto-save hook (sauvegarde instantanée)
  const { saveStatus, triggerSave } = useAutoSave({
    onSave: performSave,
    enabled: true,
  });

  // Protection contre la perte de données
  const hasUnsavedBankAccountChanges = useCallback(() => {
    return dirtyAccountIds.size > 0;
  }, [dirtyAccountIds]);

  const saveAllDirtyBankAccounts = useCallback(async () => {
    const promises = Array.from(dirtyAccountIds).map(id => {
      const account = bankAccountsRef.current.find(acc => acc.id === id);
      if (account) {
        return api.put(`/bank-accounts/${id}`, {
          name: account.name,
          color: account.color,
          startDate: account.startDate,
          startingBalance: account.startingBalance,
        });
      }
      return Promise.resolve();
    });
    await Promise.all(promises);
    setDirtyAccountIds(new Set());
  }, [dirtyAccountIds]);

  useNavigationGuard({
    hasUnsavedChanges: hasUnsavedBankAccountChanges,
    onSave: saveAllDirtyBankAccounts,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, bankAccountsRes, categoriesRes, sharedAccountsRes] = await Promise.all([
        api.get('/settings'),
        api.get('/bank-accounts'),
        api.get('/categories/user'),
        api.get('/shared-bank-accounts'),
      ]);
      if (settingsRes.data.success) {
        setSettings(settingsRes.data.data);
        setGlobalSettings(settingsRes.data.data);
      }
      if (bankAccountsRes.data.success) {
        setBankAccounts(bankAccountsRes.data.data);
        setGlobalBankAccounts(bankAccountsRes.data.data);
      }
      if (categoriesRes.data.success) {
        setUserCategories(categoriesRes.data.data);
      }
      if (sharedAccountsRes.data.success) {
        setSharedAccounts(sharedAccountsRes.data.data);
        setGlobalSharedBankAccounts(sharedAccountsRes.data.data);
      }
    } catch {
      // Handle error
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUserCategories = async () => {
    try {
      const res = await api.get('/categories/user');
      if (res.data.success) {
        setUserCategories(res.data.data);
      }
    } catch {
      // Handle error
    }
  };

  // Gestion des catégories
  const handleUpdateCategory = async (
    type: 'income' | 'expense',
    categoryId: string,
    name: string,
    isSubcategory: boolean,
    parentCategoryId?: string
  ) => {
    try {
      let url = `/categories/${type}/${categoryId}`;
      if (isSubcategory && parentCategoryId) {
        url = `/categories/${type}/${parentCategoryId}/subcategories/${categoryId}`;
      }
      await api.put(url, { name });
      await fetchUserCategories();
      setEditingCategory(null);
      setEditCategoryValue('');
    } catch {
      // Handle error
    }
  };

  const handleDeleteCategory = async () => {
    if (!deleteCategoryDialog.categoryId) return;

    try {
      let url = `/categories/${deleteCategoryDialog.type}/${deleteCategoryDialog.categoryId}`;
      if (deleteCategoryDialog.isSubcategory && deleteCategoryDialog.parentCategoryId) {
        url = `/categories/${deleteCategoryDialog.type}/${deleteCategoryDialog.parentCategoryId}/subcategories/${deleteCategoryDialog.categoryId}`;
      }
      await api.delete(url);
      await fetchUserCategories();
    } catch {
      // Handle error
    } finally {
      setDeleteCategoryDialog({
        isOpen: false,
        categoryId: null,
        categoryName: '',
        usageCount: 0,
        type: 'expense',
        isSubcategory: false,
      });
    }
  };

  const openDeleteCategoryDialog = (
    category: UserCategory | UserSubcategory,
    type: 'income' | 'expense',
    isSubcategory: boolean,
    parentCategoryId?: string
  ) => {
    setDeleteCategoryDialog({
      isOpen: true,
      categoryId: category.id,
      categoryName: category.name,
      usageCount: category.usageCount,
      type,
      isSubcategory,
      parentCategoryId,
    });
  };

  const startEditCategory = (category: UserCategory | UserSubcategory) => {
    setEditingCategory(category.id);
    setEditCategoryValue(category.name);
  };

  const cancelEditCategory = () => {
    setEditingCategory(null);
    setEditCategoryValue('');
  };

  // Ouvrir le modal de visualisation des opérations d'une catégorie
  const openOperationsModal = (
    category: UserCategory | UserSubcategory,
    categoryType: 'income' | 'expense',
    isSubcategory: boolean
  ) => {
    setOperationsModal({
      isOpen: true,
      categoryId: category.id,
      categoryName: category.name,
      categoryType,
      isSubcategory,
    });
  };

  // Ouvrir le dialog de déplacement de sous-catégorie
  const openMoveSubcategoryDialog = (
    subcategory: UserSubcategory,
    parentCategoryId: string,
    type: 'income' | 'expense'
  ) => {
    setMoveSubcategoryState({
      isOpen: true,
      subcategoryId: subcategory.id,
      subcategoryName: subcategory.name,
      currentParentId: parentCategoryId,
      type,
    });
    setMoveTargetCategoryId('');
  };

  // Exécuter le déplacement de sous-catégorie
  const handleMoveSubcategory = async () => {
    if (!moveTargetCategoryId || movingSubcategory) return;
    setMovingSubcategory(true);
    try {
      await api.put(
        `/categories/${moveSubcategoryState.type}/${moveSubcategoryState.currentParentId}/subcategories/${moveSubcategoryState.subcategoryId}/move`,
        { targetCategoryId: moveTargetCategoryId }
      );
      await fetchUserCategories();
      setMoveSubcategoryState({ isOpen: false, subcategoryId: '', subcategoryName: '', currentParentId: '', type: 'expense' });
    } catch {
      // Handle error
    } finally {
      setMovingSubcategory(false);
    }
  };

  // Ouvrir la réassignation depuis le dialog de suppression
  const handleReassignFromDelete = () => {
    const { categoryId, categoryName, type, isSubcategory } = deleteCategoryDialog;
    if (!categoryId) return;
    // Fermer le dialog de suppression
    setDeleteCategoryDialog({ isOpen: false, categoryId: null, categoryName: '', usageCount: 0, type: 'expense', isSubcategory: false });
    // Ouvrir le modal d'opérations
    setOperationsModal({ isOpen: true, categoryId, categoryName, categoryType: type, isSubcategory });
  };

  // Gestion des comptes bancaires
  const handleAddBankAccount = async () => {
    if (!newAccountName.trim()) return;
    try {
      const response = await api.post('/bank-accounts', {
        name: newAccountName.trim(),
        color: BANK_COLORS[bankAccounts.length % BANK_COLORS.length],
        isDefault: bankAccounts.length === 0,
      });
      if (response.data.success) {
        const updated = [...bankAccounts, response.data.data];
        setBankAccounts(updated);
        setGlobalBankAccounts(updated);
        setNewAccountName('');
        setShowAddAccount(false);
      }
    } catch {
      // Handle error
    }
  };

  // Mise à jour locale d'un compte bancaire (sans sauvegarde immédiate)
  const handleLocalBankAccountChange = (id: string, updates: Partial<BankAccount>) => {
    setBankAccounts(prev => prev.map(acc =>
      acc.id === id ? { ...acc, ...updates } : acc
    ));
    // Marquer le compte comme modifié
    setDirtyAccountIds(prev => new Set(prev).add(id));
  };

  // Sauvegarde d'un compte bancaire au blur
  const handleSaveBankAccount = async (id: string) => {
    // Ne sauvegarder que si le compte a été modifié
    if (!dirtyAccountIds.has(id)) return;

    const account = bankAccountsRef.current.find(acc => acc.id === id);
    if (!account) return;

    setBankAccountSaveStatus('saving');
    try {
      const response = await api.put(`/bank-accounts/${id}`, {
        name: account.name,
        color: account.color,
        startDate: account.startDate,
        startingBalance: account.startingBalance,
      });
      if (response.data.success) {
        // Retirer le compte de la liste des modifiés
        setDirtyAccountIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(id);
          return newSet;
        });
        setGlobalBankAccounts(bankAccountsRef.current);
        setBankAccountSaveStatus('saved');
        setTimeout(() => setBankAccountSaveStatus('idle'), 2000);
      }
    } catch {
      setBankAccountSaveStatus('error');
      setTimeout(() => setBankAccountSaveStatus('idle'), 2000);
    }
  };

  // Mise à jour immédiate d'un compte bancaire (pour couleur et isDefault)
  const handleUpdateBankAccount = async (id: string, updates: Partial<BankAccount>) => {
    setBankAccountSaveStatus('saving');
    try {
      const response = await api.put(`/bank-accounts/${id}`, updates);
      if (response.data.success) {
        const updated = bankAccounts.map(acc =>
          acc.id === id ? { ...acc, ...response.data.data } :
          (updates.isDefault ? { ...acc, isDefault: false } : acc)
        );
        setBankAccounts(updated);
        setGlobalBankAccounts(updated);
        setBankAccountSaveStatus('saved');
        setTimeout(() => setBankAccountSaveStatus('idle'), 2000);
      }
    } catch {
      setBankAccountSaveStatus('error');
      setTimeout(() => setBankAccountSaveStatus('idle'), 2000);
    }
  };

  const handleDeleteBankAccount = async (id: string) => {
    const account = bankAccounts.find(acc => acc.id === id);
    if (!account) return;

    try {
      const countResponse = await api.get(`/bank-accounts/${id}/operations-count`);
      const operationsCount = countResponse.data.success ? countResponse.data.data.count : 0;

      setDeleteDialog({
        isOpen: true,
        accountId: id,
        accountName: account.name,
        operationsCount,
      });
    } catch {
      setDeleteDialog({
        isOpen: true,
        accountId: id,
        accountName: account.name,
        operationsCount: 0,
      });
    }
  };

  const confirmDeleteBankAccount = async () => {
    if (!deleteDialog.accountId) return;

    try {
      const response = await api.delete(`/bank-accounts/${deleteDialog.accountId}`);
      if (response.data.success) {
        let updatedAccounts = bankAccounts.filter(acc => acc.id !== deleteDialog.accountId);

        if (response.data.data.newDefaultAccount) {
          updatedAccounts = [response.data.data.newDefaultAccount, ...updatedAccounts];
        }

        if (updatedAccounts.length > 0) {
          const hasDefault = updatedAccounts.some(acc => acc.isDefault);
          if (!hasDefault) {
            updatedAccounts[0].isDefault = true;
          }
        }

        setBankAccounts(updatedAccounts);
        setGlobalBankAccounts(updatedAccounts);
      }
    } catch {
      // Handle error
    } finally {
      setDeleteDialog({
        isOpen: false,
        accountId: null,
        accountName: '',
        operationsCount: 0,
      });
    }
  };

  const handleSetDefaultAccount = async (id: string) => {
    try {
      const response = await api.put(`/bank-accounts/${id}/set-default`);
      if (response.data.success) {
        const updated = bankAccounts.map(acc => ({
          ...acc,
          isDefault: acc.id === id,
        }));
        setBankAccounts(updated);
        setGlobalBankAccounts(updated);
      }
    } catch {
      // Handle error
    }
  };

  // ============================================
  // CRUD comptes partagés
  // ============================================

  const handleAddSharedAccount = async () => {
    if (!newSharedAccountName.trim()) return;
    try {
      const response = await api.post('/shared-bank-accounts', {
        name: newSharedAccountName.trim(),
        color: BANK_COLORS[sharedAccounts.length % BANK_COLORS.length],
        isDefault: sharedAccounts.length === 0,
      });
      if (response.data.success) {
        const updated = [...sharedAccounts, response.data.data];
        setSharedAccounts(updated);
        setGlobalSharedBankAccounts(updated);
        setNewSharedAccountName('');
        setShowAddSharedAccount(false);
      }
    } catch {
      // Handle error
    }
  };

  const handleLocalSharedAccountChange = (id: string, updates: Partial<SharedBankAccount>) => {
    setSharedAccounts(prev => prev.map(acc =>
      acc.id === id ? { ...acc, ...updates } : acc
    ));
    setDirtySharedAccountIds(prev => new Set(prev).add(id));
  };

  const handleSaveSharedAccount = async (id: string) => {
    if (!dirtySharedAccountIds.has(id)) return;

    const account = sharedAccountsRef.current.find(acc => acc.id === id);
    if (!account) return;

    // saving
    try {
      const response = await api.put(`/shared-bank-accounts/${id}`, {
        name: account.name,
        color: account.color,
        startDate: account.startDate,
        startingBalance: account.startingBalance,
      });
      if (response.data.success) {
        setDirtySharedAccountIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(id);
          return newSet;
        });
        setGlobalSharedBankAccounts(sharedAccountsRef.current);
        // saved
      }
    } catch {
      // error
    }
  };

  const handleUpdateSharedAccount = async (id: string, updates: Partial<SharedBankAccount>) => {
    // saving
    try {
      const response = await api.put(`/shared-bank-accounts/${id}`, updates);
      if (response.data.success) {
        const updated = sharedAccounts.map(acc =>
          acc.id === id ? { ...acc, ...response.data.data } :
          (updates.isDefault ? { ...acc, isDefault: false } : acc)
        );
        setSharedAccounts(updated);
        setGlobalSharedBankAccounts(updated);
        // saved
      }
    } catch {
      // error
    }
  };

  const handleDeleteSharedAccount = async (id: string) => {
    const account = sharedAccounts.find(acc => acc.id === id);
    if (!account) return;

    try {
      const countResponse = await api.get(`/shared-bank-accounts/${id}/operations-count`);
      const operationsCount = countResponse.data.success ? countResponse.data.data.count : 0;

      setDeleteSharedDialog({
        isOpen: true,
        accountId: id,
        accountName: account.name,
        operationsCount,
      });
    } catch {
      setDeleteSharedDialog({
        isOpen: true,
        accountId: id,
        accountName: account.name,
        operationsCount: 0,
      });
    }
  };

  const confirmDeleteSharedAccount = async () => {
    if (!deleteSharedDialog.accountId) return;

    try {
      const response = await api.delete(`/shared-bank-accounts/${deleteSharedDialog.accountId}`);
      if (response.data.success) {
        let updatedAccounts = sharedAccounts.filter(acc => acc.id !== deleteSharedDialog.accountId);

        if (response.data.data.newDefaultAccount) {
          updatedAccounts = [response.data.data.newDefaultAccount, ...updatedAccounts];
        }

        if (updatedAccounts.length > 0) {
          const hasDefault = updatedAccounts.some(acc => acc.isDefault);
          if (!hasDefault) {
            updatedAccounts[0].isDefault = true;
          }
        }

        setSharedAccounts(updatedAccounts);
        setGlobalSharedBankAccounts(updatedAccounts);
      }
    } catch {
      // Handle error
    } finally {
      setDeleteSharedDialog({
        isOpen: false,
        accountId: null,
        accountName: '',
        operationsCount: 0,
      });
    }
  };

  const handleSetDefaultSharedAccount = async (id: string) => {
    try {
      const response = await api.put(`/shared-bank-accounts/${id}/set-default`);
      if (response.data.success) {
        const updated = sharedAccounts.map(acc => ({
          ...acc,
          isDefault: acc.id === id,
        }));
        setSharedAccounts(updated);
        setGlobalSharedBankAccounts(updated);
      }
    } catch {
      // Handle error
    }
  };

  const handleLivingTypeChange = (type: LivingType) => {
    setSettings(s => s ? { ...s, livingType: type } : null);
    triggerSave();
  };

  const handleCalculationModeChange = (mode: 'proportional' | 'equal' | 'fixed') => {
    setSettings(s => s ? { ...s, calculationMode: mode } : null);
    triggerSave();
  };

  const handleAccountTypeChange = (accountType: AccountType) => {
    setSettings(s => s ? { ...s, accountType } : null);
    triggerSave();
  };

  const handleReferencePartnerChange = (ref: 'partner1' | 'partner2') => {
    setSettings(s => s ? { ...s, customReferencePartner: ref } : null);
    triggerSave();
  };

  const handlePercentageChange = (value: number) => {
    if (!settings) return;
    const clamped = Math.max(0, Math.min(100, value));
    const ref = settings.customReferencePartner ?? 'partner1';
    const partner1 = ref === 'partner1' ? clamped : 100 - clamped;
    setSettings({
      ...settings,
      partner1Percentage: partner1,
      partner2Percentage: 100 - partner1,
    });
    triggerSave();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Toast de sauvegarde en position fixe */}
      <SaveToast status={bankAccountSaveStatus !== 'idle' ? bankAccountSaveStatus : saveStatus} />

      {/* En-tête — reprend la section sélectionnée dans le menu */}
      <div data-tutorial="reglages-title">
        <div className="flex flex-wrap items-center gap-3">
          {activeTab === 'shared' && <Users className="w-6 h-6 text-primary-600" />}
          {activeTab === 'personal' && <Wallet className="w-6 h-6 text-green-600" />}
          {activeTab === 'categories' && <Tag className="w-6 h-6 text-purple-600" />}
          <h1 className="text-2xl font-bold text-gray-900">
            {activeTab === 'shared' && terms.settingsLabel}
            {activeTab === 'personal' && t('sections.personal')}
            {activeTab === 'categories' && t('sections.categoriesManagement')}
          </h1>
          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
            {activeTab === 'shared' && t('sections.sharedBadge')}
            {activeTab === 'personal' && t('sections.personalBadge')}
            {activeTab === 'categories' && t('sections.categoriesBadge')}
          </span>
        </div>
        <p className="text-gray-600 mt-1">
          {activeTab === 'shared' && t('description')}
          {activeTab === 'personal' && t('sections.personalDescription')}
          {activeTab === 'categories' && t('sections.categoriesDescription')}
        </p>
      </div>

      {/* Section: Réglages du foyer */}
      {activeTab === 'shared' && (
      <div className="space-y-6">
        {/* Grille des réglages du foyer en 2 colonnes sur grand écran */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* Colonne 1 : le foyer — lien avec le partenaire, type de foyer, noms, revenu */}
          <div className="space-y-6">
            {/* Demandes reçues, puis invitation (compte solo) ou compte lié */}
            <PartnerLinkCard
              terms={terms}
              dateLocale={dateLocale}
              onMessage={notify}
              onLinkChanged={fetchData}
            />

            {/* Type de vie (Couple / Colocation) */}
            <div className="card">
              <h3 className="text-base font-semibold text-gray-900 mb-4">{t('livingType.title')}</h3>
              <p className="text-sm text-gray-500 mb-4">
                {t('livingType.description')}
              </p>
              <div className="grid grid-cols-2 gap-3">
                {getLivingTypes(t).map((type) => {
                  const Icon = type.icon;
                  const isSelected = settings?.livingType === type.value;
                  return (
                    <button
                      key={type.value}
                      onClick={() => handleLivingTypeChange(type.value)}
                      className={`flex flex-col items-center gap-2 p-4 border-2 rounded-lg transition-all ${
                        isSelected
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-200 hover:border-gray-300 text-gray-600'
                      }`}
                    >
                      <Icon className={`w-6 h-6 ${isSelected ? 'text-primary-600' : 'text-gray-400'}`} />
                      <span className="font-medium">{type.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Noms des membres du foyer */}
            <HouseholdMembersCard terms={terms} onMessage={notify} />

            {/* Revenu du partenaire 2, tant qu'il n'a pas de compte */}
            {!hasPartner && (
              <PartnerIncomeCard
                partnerName={user?.partner2FirstName || terms.partner2Default}
                baseIncome={settings?.partner2ManualMonthlyIncome ?? null}
                dateLocale={dateLocale}
                onSettingsUpdated={(updated) => {
                  // La sauvegarde auto renvoie l'objet settings entier : il doit
                  // porter le nouveau revenu, sinon elle écraserait celui-ci.
                  setSettings(updated);
                  setGlobalSettings(updated);
                }}
                onMessage={notify}
              />
            )}
          </div>

          {/* Colonne 2 : le fonctionnement — répartition, type de gestion, comptes communs */}
          <div className="space-y-6">
            {/* Mode de calcul */}
            <div className="card">
              <h3 className="text-base font-semibold text-gray-900 mb-4">{t('splitMode.title')}</h3>
              <div className="space-y-3">
                {([
                  ['proportional', t('splitMode.proportional'), t('splitMode.proportionalDesc')],
                  ['equal', t('splitMode.equal'), t('splitMode.equalDesc')],
                  ['fixed', t('splitMode.custom'), t('splitMode.customDesc')],
                ] as ['proportional' | 'equal' | 'fixed', string, string][]).map(([value, label, desc]) => (
                  <OptionToggle
                    key={value}
                    selected={settings?.calculationMode === value}
                    onToggle={() => handleCalculationModeChange(value)}
                    className="items-start"
                  >
                    <span className="flex flex-col gap-0.5">
                      <span className="font-medium text-sm">{label}</span>
                      <span className="text-xs" style={{ color: 'var(--text-3)' }}>{desc}</span>
                    </span>
                  </OptionToggle>
                ))}
              </div>

              {/* Répartition personnalisée */}
              {settings?.calculationMode === 'fixed' && (
                <div className="mt-4 pt-4 border-t">
                  {/* Partenaire de référence */}
                  <div className="mb-4">
                    <span className="field-label">{t('splitMode.referencePartner')}</span>
                    <SegmentedControl
                      block
                      ariaLabel={t('splitMode.referencePartner')}
                      value={settings.customReferencePartner ?? 'partner1'}
                      onChange={handleReferencePartnerChange}
                      options={[
                        { value: 'partner1', label: partner1Name },
                        { value: 'partner2', label: partner2Name },
                      ]}
                    />
                  </div>

                  {/* Slider de répartition */}
                  {(() => {
                    const ref = settings.customReferencePartner ?? 'partner1';
                    const refName = ref === 'partner1' ? partner1Name : partner2Name;
                    const otherName = ref === 'partner1' ? partner2Name : partner1Name;
                    const refPct = ref === 'partner1' ? settings.partner1Percentage : settings.partner2Percentage;
                    const otherPct = 100 - refPct;
                    return (
                      <div className="space-y-4">
                        <div>
                          <div className="flex justify-between mb-1">
                            <span className="text-sm text-gray-600">{refName}</span>
                            <span className="text-sm font-semibold text-primary-600">{refPct}%</span>
                          </div>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={refPct}
                            onChange={(e) => handlePercentageChange(parseInt(e.target.value))}
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
                          />
                        </div>
                        <div>
                          <div className="flex justify-between mb-1">
                            <span className="text-sm text-gray-600">{otherName}</span>
                            <span className="text-sm font-semibold text-green-600">{otherPct}%</span>
                          </div>
                          <div className="w-full h-2 bg-gray-200 rounded-lg overflow-hidden">
                            <div
                              className="h-full bg-green-500 rounded-lg"
                              style={{ width: `${otherPct}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            <div className="card" data-tutorial="account-type">
              <h3 className="text-base font-semibold text-gray-900 mb-4">{t('accountType.title')}</h3>
              <div className="space-y-3">
                {accountTypes.map((type) => {
                  const Icon = type.icon;
                  return (
                    <OptionToggle
                      key={type.value}
                      selected={settings?.accountType === type.value}
                      onToggle={() => handleAccountTypeChange(type.value)}
                      className="items-start"
                    >
                      <span className="flex flex-col gap-1">
                        <span className="flex items-center gap-2">
                          <Icon className="w-4 h-4" style={{ color: 'var(--pa)' }} />
                          <span className="font-medium text-sm">{type.label}</span>
                        </span>
                        <span className="text-xs" style={{ color: 'var(--text-3)' }}>{type.description}</span>
                      </span>
                    </OptionToggle>
                  );
                })}
              </div>
            </div>

            {/* Comptes communs */}
            <div className="card" data-tutorial="budget-start-date">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-primary-600" />
                  <h3 className="text-base font-semibold text-gray-900">{t('sharedBankAccounts.title')}</h3>
                </div>
                {!showAddSharedAccount && canCreate('sharedAccounts') && (
                  <button
                    onClick={() => setShowAddSharedAccount(true)}
                    className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium"
                  >
                    <Plus className="w-3 h-3" />
                    {t('sharedBankAccounts.add')}
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-500 mb-4">
                {t('sharedBankAccounts.description')}
              </p>

              {/* Formulaire d'ajout */}
              {showAddSharedAccount && (
                <div className="p-3 bg-gray-50 rounded-lg mb-4">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder={t('bankAccounts.namePlaceholder')}
                      value={newSharedAccountName}
                      onChange={(e) => setNewSharedAccountName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddSharedAccount()}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                      autoFocus
                    />
                    <button
                      onClick={handleAddSharedAccount}
                      disabled={!newSharedAccountName.trim()}
                      className="px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      OK
                    </button>
                    <button
                      onClick={() => { setShowAddSharedAccount(false); setNewSharedAccountName(''); }}
                      className="px-3 py-2 text-gray-600 hover:text-gray-800 text-sm"
                    >
                      X
                    </button>
                  </div>
                </div>
              )}

              {/* Liste des comptes */}
              {sharedAccounts.length === 0 && !showAddSharedAccount ? (
                <div className="text-center py-6 text-gray-500">
                  <Users className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">{t('sharedBankAccounts.empty')}</p>
                  <button
                    onClick={() => setShowAddSharedAccount(true)}
                    className="mt-2 text-primary-600 hover:text-primary-700 font-medium text-sm"
                  >
                    {t('sharedBankAccounts.addAccount')}
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {sharedAccounts.map((account) => (
                    <div
                      key={account.id}
                      className="border rounded-lg p-3 hover:border-gray-300 transition-colors"
                      style={{ borderLeftWidth: '4px', borderLeftColor: account.color }}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          {/* Sélecteur de couleur */}
                          <div className="relative">
                            <button
                              onClick={() => setEditingSharedAccount(editingSharedAccount === account.id ? null : account.id)}
                              className="w-6 h-6 rounded-full border-2 border-white shadow hover:scale-110 transition-transform"
                              style={{ backgroundColor: account.color }}
                              title={t('bankAccounts.changeColor')}
                            />
                            {editingSharedAccount === account.id && (
                              <div className="absolute top-8 left-0 z-10 p-2 bg-white rounded-lg shadow-lg border flex gap-1 flex-wrap w-28">
                                {BANK_COLORS.map((color) => (
                                  <button
                                    key={color}
                                    onClick={() => {
                                      handleUpdateSharedAccount(account.id, { color });
                                      setEditingSharedAccount(null);
                                    }}
                                    className={`w-5 h-5 rounded-full hover:scale-110 transition-transform ${
                                      account.color === color ? 'ring-2 ring-offset-1 ring-gray-400' : ''
                                    }`}
                                    style={{ backgroundColor: color }}
                                  />
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-1 group/name">
                            <input
                              type="text"
                              value={account.name}
                              onChange={(e) => handleLocalSharedAccountChange(account.id, { name: e.target.value })}
                              onBlur={() => handleSaveSharedAccount(account.id)}
                              className="font-medium text-gray-900 bg-transparent px-1.5 py-0.5 -ml-1.5 rounded border border-transparent hover:border-gray-300 focus:border-primary-500 focus:bg-white focus:ring-1 focus:ring-primary-500 text-sm w-32 transition-colors"
                              placeholder={t('bankAccounts.namePlaceholder')}
                            />
                            <Pencil className="w-3 h-3 text-gray-300 group-hover/name:text-gray-400 transition-colors" />
                            {account.isDefault && (
                              <span className="text-[10px] bg-primary-100 text-primary-700 px-1.5 py-0.5 rounded-full">
                                {t('sharedBankAccounts.default')}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {!account.isDefault && effectivePlan === 'duo' && (
                            <button
                              onClick={() => handleSetDefaultSharedAccount(account.id)}
                              className="p-1 text-gray-400 hover:text-primary-600 transition-colors"
                              title={t('bankAccounts.setDefault')}
                            >
                              <Star className="w-3 h-3" />
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteSharedAccount(account.id)}
                            className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                            title={t('bankAccounts.deleteAccount')}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="flex items-center gap-1 text-xs font-medium text-gray-600 mb-1">
                            <Calendar className="w-3 h-3" />
                            {t('sharedBankAccounts.startDate')}
                          </label>
                          <input
                            type="date"
                            value={formatDateForInput(account.startDate)}
                            onChange={(e) => handleLocalSharedAccountChange(account.id, {
                              startDate: e.target.value ? new Date(e.target.value).toISOString() : null
                            })}
                            onBlur={() => handleSaveSharedAccount(account.id)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-primary-500"
                          />
                        </div>
                        <div>
                          <label className="flex items-center gap-1 text-xs font-medium text-gray-600 mb-1">
                            <Banknote className="w-3 h-3" />
                            {t('sharedBankAccounts.startingBalance')}
                          </label>
                          <div className="relative">
                            <AmountInput
                              value={account.startingBalance?.toString() || ''}
                              onChange={(value) => handleLocalSharedAccountChange(account.id, {
                                startingBalance: value ? parseFloat(value) : 0
                              })}
                              onBlur={() => handleSaveSharedAccount(account.id)}
                              className="w-full pr-6 !text-xs"
                            />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">€</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Section: Réglages personnels */}
      {activeTab === 'personal' && settings?.accountType !== 'joint_only' && (
        <div className="space-y-6">
          {/* Grille des réglages personnels */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-gray-900">{t('bankAccounts.title')}</h3>
                {!showAddAccount && canCreate('personalAccounts') && (
                  <button
                    onClick={() => setShowAddAccount(true)}
                    className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium"
                  >
                    <Plus className="w-3 h-3" />
                    {t('bankAccounts.add')}
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-500 mb-4">
                {t('bankAccounts.description')}
              </p>

              {/* Formulaire d'ajout */}
              {showAddAccount && (
                <div className="p-3 bg-gray-50 rounded-lg mb-4">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder={t('bankAccounts.namePlaceholder')}
                      value={newAccountName}
                      onChange={(e) => setNewAccountName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddBankAccount()}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                      autoFocus
                    />
                    <button
                      onClick={handleAddBankAccount}
                      disabled={!newAccountName.trim()}
                      className="px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      OK
                    </button>
                    <button
                      onClick={() => { setShowAddAccount(false); setNewAccountName(''); }}
                      className="px-3 py-2 text-gray-600 hover:text-gray-800 text-sm"
                    >
                      X
                    </button>
                  </div>
                </div>
              )}

              {/* Liste des comptes */}
              {bankAccounts.length === 0 && !showAddAccount ? (
                <div className="text-center py-6 text-gray-500">
                  <Wallet className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">{t('bankAccounts.empty')}</p>
                  <button
                    onClick={() => setShowAddAccount(true)}
                    className="mt-2 text-primary-600 hover:text-primary-700 font-medium text-sm"
                  >
                    {t('bankAccounts.addAccount')}
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {bankAccounts.map((account) => (
                    <div
                      key={account.id}
                      className="border rounded-lg p-3 hover:border-gray-300 transition-colors"
                      style={{ borderLeftWidth: '4px', borderLeftColor: account.color }}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          {/* Sélecteur de couleur */}
                          <div className="relative">
                            <button
                              onClick={() => setEditingAccount(editingAccount === account.id ? null : account.id)}
                              className="w-6 h-6 rounded-full border-2 border-white shadow hover:scale-110 transition-transform"
                              style={{ backgroundColor: account.color }}
                              title={t('bankAccounts.changeColor')}
                            />
                            {editingAccount === account.id && (
                              <div className="absolute top-8 left-0 z-10 p-2 bg-white rounded-lg shadow-lg border flex gap-1 flex-wrap w-28">
                                {BANK_COLORS.map((color) => (
                                  <button
                                    key={color}
                                    onClick={() => {
                                      handleUpdateBankAccount(account.id, { color });
                                      setEditingAccount(null);
                                    }}
                                    className={`w-5 h-5 rounded-full hover:scale-110 transition-transform ${
                                      account.color === color ? 'ring-2 ring-offset-1 ring-gray-400' : ''
                                    }`}
                                    style={{ backgroundColor: color }}
                                  />
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-1 group/name">
                            <input
                              type="text"
                              value={account.name}
                              onChange={(e) => handleLocalBankAccountChange(account.id, { name: e.target.value })}
                              onBlur={() => handleSaveBankAccount(account.id)}
                              className="font-medium text-gray-900 bg-transparent px-1.5 py-0.5 -ml-1.5 rounded border border-transparent hover:border-gray-300 focus:border-primary-500 focus:bg-white focus:ring-1 focus:ring-primary-500 text-sm w-32 transition-colors"
                              placeholder={t('bankAccounts.namePlaceholder')}
                            />
                            <Pencil className="w-3 h-3 text-gray-300 group-hover/name:text-gray-400 transition-colors" />
                            {account.isDefault && (
                              <span className="text-[10px] bg-primary-100 text-primary-700 px-1.5 py-0.5 rounded-full">
                                {t('bankAccounts.default')}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {!account.isDefault && effectivePlan !== 'free' && (
                            <button
                              onClick={() => handleSetDefaultAccount(account.id)}
                              className="p-1 text-gray-400 hover:text-primary-600 transition-colors"
                              title={t('bankAccounts.setDefault')}
                            >
                              <Star className="w-3 h-3" />
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteBankAccount(account.id)}
                            className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                            title={t('bankAccounts.deleteAccount')}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="flex items-center gap-1 text-xs font-medium text-gray-600 mb-1">
                            <Calendar className="w-3 h-3" />
                            {t('bankAccounts.startDate')}
                          </label>
                          <input
                            type="date"
                            value={formatDateForInput(account.startDate)}
                            onChange={(e) => handleLocalBankAccountChange(account.id, {
                              startDate: e.target.value ? new Date(e.target.value).toISOString() : null
                            })}
                            onBlur={() => handleSaveBankAccount(account.id)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-primary-500"
                          />
                        </div>
                        <div>
                          <label className="flex items-center gap-1 text-xs font-medium text-gray-600 mb-1">
                            <Banknote className="w-3 h-3" />
                            {t('bankAccounts.startingBalance')}
                          </label>
                          <div className="relative">
                            <AmountInput
                              value={account.startingBalance?.toString() || ''}
                              onChange={(value) => handleLocalBankAccountChange(account.id, {
                                startingBalance: value ? parseFloat(value) : 0
                              })}
                              onBlur={() => handleSaveBankAccount(account.id)}
                              className="w-full pr-6 !text-xs"
                            />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">€</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Section: Gestion des catégories */}
      {activeTab === 'categories' && (
      <div className="space-y-6" data-tutorial="categories">
        {/* Division horizontale : Compte Commun / Budget Personnel */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* Colonne 1 : Catégories du compte commun (couple) */}
          <div className="card">
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-5 h-5 text-primary-600" />
              <h3 className="text-base font-semibold text-gray-900">{t('categories.shared')}</h3>
            </div>

            {/* Sous-division : Revenus / Dépenses */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Revenus du couple */}
              <div>
                <div className="flex items-center gap-1 mb-2">
                  <TrendingUp className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium text-gray-700">{t('categories.income')}</span>
                </div>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {userCategories.income.filter(c => c.isCouple).length === 0 ? (
                    <p className="text-xs text-gray-400 italic py-2">{t('categories.noCategories')}</p>
                  ) : (
                    userCategories.income.filter(c => c.isCouple).map((cat) => (
                      <div key={cat.id}>
                        <CategoryItem
                          name={cat.name}
                          categoryId={cat.id}
                          isDefaultCategory={cat.isDefault}
                          usageCount={cat.usageCount}
                          isEditing={editingCategory === cat.id}
                          editValue={editCategoryValue}
                          onEditChange={setEditCategoryValue}
                          onEditStart={() => startEditCategory(cat)}
                          onEditSave={() => handleUpdateCategory('income', cat.id, editCategoryValue, false)}
                          onEditCancel={cancelEditCategory}
                          onDelete={() => openDeleteCategoryDialog(cat, 'income', false)}
                          onViewOperations={() => openOperationsModal(cat, 'income', false)}
                          readOnly={cat.isDefault}
                        />
                        {cat.subcategories.filter(s => s.isCouple).map((sub) => (
                          <CategoryItem
                            key={sub.id}
                            name={sub.name}
                            categoryId={sub.id}
                            isDefaultCategory={sub.isDefault}
                            usageCount={sub.usageCount}
                            isEditing={editingCategory === sub.id}
                            editValue={editCategoryValue}
                            onEditChange={setEditCategoryValue}
                            onEditStart={() => startEditCategory(sub)}
                            onEditSave={() => handleUpdateCategory('income', sub.id, editCategoryValue, true, cat.id)}
                            onEditCancel={cancelEditCategory}
                            onDelete={() => openDeleteCategoryDialog(sub, 'income', true, cat.id)}
                            onViewOperations={() => openOperationsModal(sub, 'income', true)}
                            onMoveSubcategory={() => openMoveSubcategoryDialog(sub, cat.id, 'income')}
                            isSubcategory
                          />
                        ))}
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Dépenses du couple */}
              <div>
                <div className="flex items-center gap-1 mb-2">
                  <TrendingDown className="w-4 h-4 text-red-600" />
                  <span className="text-sm font-medium text-gray-700">{t('categories.expenses')}</span>
                </div>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {userCategories.expense.filter(c => c.isCouple).length === 0 ? (
                    <p className="text-xs text-gray-400 italic py-2">{t('categories.noCategories')}</p>
                  ) : (
                    userCategories.expense.filter(c => c.isCouple).map((cat) => (
                      <div key={cat.id}>
                        <CategoryItem
                          name={cat.name}
                          categoryId={cat.id}
                          isDefaultCategory={cat.isDefault}
                          usageCount={cat.usageCount}
                          isEditing={editingCategory === cat.id}
                          editValue={editCategoryValue}
                          onEditChange={setEditCategoryValue}
                          onEditStart={() => startEditCategory(cat)}
                          onEditSave={() => handleUpdateCategory('expense', cat.id, editCategoryValue, false)}
                          onEditCancel={cancelEditCategory}
                          onDelete={() => openDeleteCategoryDialog(cat, 'expense', false)}
                          onViewOperations={() => openOperationsModal(cat, 'expense', false)}
                          readOnly={cat.isDefault}
                        />
                        {cat.subcategories.filter(s => s.isCouple).map((sub) => (
                          <CategoryItem
                            key={sub.id}
                            name={sub.name}
                            categoryId={sub.id}
                            isDefaultCategory={sub.isDefault}
                            usageCount={sub.usageCount}
                            isEditing={editingCategory === sub.id}
                            editValue={editCategoryValue}
                            onEditChange={setEditCategoryValue}
                            onEditStart={() => startEditCategory(sub)}
                            onEditSave={() => handleUpdateCategory('expense', sub.id, editCategoryValue, true, cat.id)}
                            onEditCancel={cancelEditCategory}
                            onDelete={() => openDeleteCategoryDialog(sub, 'expense', true, cat.id)}
                            onViewOperations={() => openOperationsModal(sub, 'expense', true)}
                            onMoveSubcategory={() => openMoveSubcategoryDialog(sub, cat.id, 'expense')}
                            isSubcategory
                          />
                        ))}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Colonne 2 : Catégories personnelles (userId) */}
          <div className="card">
            <div className="flex items-center gap-2 mb-4">
              <Wallet className="w-5 h-5 text-green-600" />
              <h3 className="text-base font-semibold text-gray-900">{t('categories.personal')}</h3>
            </div>

            {/* Sous-division : Revenus / Dépenses */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Revenus personnels */}
              <div>
                <div className="flex items-center gap-1 mb-2">
                  <TrendingUp className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium text-gray-700">{t('categories.income')}</span>
                </div>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {userCategories.income.filter(c => c.isPersonal).length === 0 ? (
                    <p className="text-xs text-gray-400 italic py-2">{t('categories.noCategories')}</p>
                  ) : (
                    userCategories.income.filter(c => c.isPersonal).map((cat) => (
                      <div key={cat.id}>
                        <CategoryItem
                          name={cat.name}
                          categoryId={cat.id}
                          isDefaultCategory={cat.isDefault}
                          usageCount={cat.usageCount}
                          isEditing={editingCategory === cat.id}
                          editValue={editCategoryValue}
                          onEditChange={setEditCategoryValue}
                          onEditStart={() => startEditCategory(cat)}
                          onEditSave={() => handleUpdateCategory('income', cat.id, editCategoryValue, false)}
                          onEditCancel={cancelEditCategory}
                          onDelete={() => openDeleteCategoryDialog(cat, 'income', false)}
                          onViewOperations={() => openOperationsModal(cat, 'income', false)}
                          readOnly={cat.isDefault}
                        />
                        {cat.subcategories.filter(s => s.isPersonal).map((sub) => (
                          <CategoryItem
                            key={sub.id}
                            name={sub.name}
                            categoryId={sub.id}
                            isDefaultCategory={sub.isDefault}
                            usageCount={sub.usageCount}
                            isEditing={editingCategory === sub.id}
                            editValue={editCategoryValue}
                            onEditChange={setEditCategoryValue}
                            onEditStart={() => startEditCategory(sub)}
                            onEditSave={() => handleUpdateCategory('income', sub.id, editCategoryValue, true, cat.id)}
                            onEditCancel={cancelEditCategory}
                            onDelete={() => openDeleteCategoryDialog(sub, 'income', true, cat.id)}
                            onViewOperations={() => openOperationsModal(sub, 'income', true)}
                            onMoveSubcategory={() => openMoveSubcategoryDialog(sub, cat.id, 'income')}
                            isSubcategory
                          />
                        ))}
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Dépenses personnelles */}
              <div>
                <div className="flex items-center gap-1 mb-2">
                  <TrendingDown className="w-4 h-4 text-red-600" />
                  <span className="text-sm font-medium text-gray-700">{t('categories.expenses')}</span>
                </div>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {userCategories.expense.filter(c => c.isPersonal).length === 0 ? (
                    <p className="text-xs text-gray-400 italic py-2">{t('categories.noCategories')}</p>
                  ) : (
                    userCategories.expense.filter(c => c.isPersonal).map((cat) => (
                      <div key={cat.id}>
                        <CategoryItem
                          name={cat.name}
                          categoryId={cat.id}
                          isDefaultCategory={cat.isDefault}
                          usageCount={cat.usageCount}
                          isEditing={editingCategory === cat.id}
                          editValue={editCategoryValue}
                          onEditChange={setEditCategoryValue}
                          onEditStart={() => startEditCategory(cat)}
                          onEditSave={() => handleUpdateCategory('expense', cat.id, editCategoryValue, false)}
                          onEditCancel={cancelEditCategory}
                          onDelete={() => openDeleteCategoryDialog(cat, 'expense', false)}
                          onViewOperations={() => openOperationsModal(cat, 'expense', false)}
                          readOnly={cat.isDefault}
                        />
                        {cat.subcategories.filter(s => s.isPersonal).map((sub) => (
                          <CategoryItem
                            key={sub.id}
                            name={sub.name}
                            categoryId={sub.id}
                            isDefaultCategory={sub.isDefault}
                            usageCount={sub.usageCount}
                            isEditing={editingCategory === sub.id}
                            editValue={editCategoryValue}
                            onEditChange={setEditCategoryValue}
                            onEditStart={() => startEditCategory(sub)}
                            onEditSave={() => handleUpdateCategory('expense', sub.id, editCategoryValue, true, cat.id)}
                            onEditCancel={cancelEditCategory}
                            onDelete={() => openDeleteCategoryDialog(sub, 'expense', true, cat.id)}
                            onViewOperations={() => openOperationsModal(sub, 'expense', true)}
                            onMoveSubcategory={() => openMoveSubcategoryDialog(sub, cat.id, 'expense')}
                            isSubcategory
                          />
                        ))}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Apparence — bascule de mode d'affichage (Clair / Sombre / Système), accessible à tous.
          En bas de page : c'est une préférence d'affichage, pas un réglage du budget. */}
      <div className="card">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-primary-50">
            <Palette className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('appearance.title')}</h2>
            <p className="text-sm text-gray-500">{t('appearance.subtitle')}</p>
          </div>
        </div>
        <DisplayModeToggle />
      </div>

      {/* Dialog de confirmation de suppression de compte */}
      <DeleteAccountDialog
        isOpen={deleteDialog.isOpen}
        accountName={deleteDialog.accountName}
        operationsCount={deleteDialog.operationsCount}
        onConfirm={confirmDeleteBankAccount}
        onCancel={() => setDeleteDialog({ isOpen: false, accountId: null, accountName: '', operationsCount: 0 })}
      />

      {/* Dialog de confirmation de suppression de compte partagé */}
      <DeleteAccountDialog
        isOpen={deleteSharedDialog.isOpen}
        accountName={deleteSharedDialog.accountName}
        operationsCount={deleteSharedDialog.operationsCount}
        onConfirm={confirmDeleteSharedAccount}
        onCancel={() => setDeleteSharedDialog({ isOpen: false, accountId: null, accountName: '', operationsCount: 0 })}
      />

      {/* Dialog de confirmation de suppression de catégorie */}
      <DeleteCategoryDialog
        isOpen={deleteCategoryDialog.isOpen}
        categoryName={deleteCategoryDialog.categoryName}
        usageCount={deleteCategoryDialog.usageCount}
        isSubcategory={deleteCategoryDialog.isSubcategory}
        onConfirm={handleDeleteCategory}
        onCancel={() => setDeleteCategoryDialog({
          isOpen: false,
          categoryId: null,
          categoryName: '',
          usageCount: 0,
          type: 'expense',
          isSubcategory: false,
        })}
        onReassign={handleReassignFromDelete}
      />

      {/* Modal de visualisation des opérations d'une catégorie */}
      <CategoryOperationsModal
        isOpen={operationsModal.isOpen}
        onClose={() => setOperationsModal({ isOpen: false, categoryId: '', categoryName: '', categoryType: 'expense', isSubcategory: false })}
        categoryId={operationsModal.categoryId}
        categoryName={operationsModal.categoryName}
        categoryType={operationsModal.categoryType}
        isSubcategory={operationsModal.isSubcategory}
        onReassigned={fetchUserCategories}
      />

      {/* Dialog de déplacement de sous-catégorie */}
      <MoveSubcategoryDialog
        isOpen={moveSubcategoryState.isOpen}
        subcategoryName={moveSubcategoryState.subcategoryName}
        currentParentId={moveSubcategoryState.currentParentId}
        type={moveSubcategoryState.type}
        targetCategoryId={moveTargetCategoryId}
        onTargetChange={setMoveTargetCategoryId}
        onConfirm={handleMoveSubcategory}
        onCancel={() => setMoveSubcategoryState({ isOpen: false, subcategoryId: '', subcategoryName: '', currentParentId: '', type: 'expense' })}
        isLoading={movingSubcategory}
      />
    </div>
  );
}
